// The Search tab of the task pane (spec 4 and 5): one instrument picked from
// the hub's suggestions, the full results a page at a time, switching a pick
// Find RICs marked « k sur N », and the Find RICs button.
//
// The first half is the view model: pure functions, tested in
// test/searchPane.test.ts. The second half is the Office.js and DOM glue: it
// only reads the sheet, calls the client and shows what the view model says.
//
// Rules kept here:
//   - The text goes to the hub as typed (trimmed); a RIC is written as the hub
//     spells it, never re-cased, as a plain value (`literalOf`), and every
//     cell the pane writes is marked as the add-in's own (`markOwn`).
//   - A failure or a partial answer never reads "No results".
//   - A signed-out error (isSignedOut: the add-in's own 401, not the hub's)
//     shows the pane's signed-out state, never "Search failed".

import { ApiError } from "../lib/api";
import { literalOf, markOwn } from "../lib/excelPort";
import {
  lookupIsin,
  searchAll,
  suggest,
  tellPick,
  type SearchAllAnswer,
  type SearchAllRow,
  type SuggestAnswer,
  type SuggestItem,
  type SuggestNote,
} from "../lib/searchApi";
import { createAsker, MIN_CHARS, type Asker, type AskState } from "../search/asker";
import { formatC, parseMarker } from "../search/columnC";
import { excelSheetIO, MAX_LEN, runFindRics, type RunResult } from "../search/findRics";
import { isSignedOut, resolveRow, type ResolveDeps, type Resolution } from "../search/resolve";

// --- View model ----------------------------------------------------------------

/** The suggestion groups, in the order the full search offers them. */
export const KIND_ORDER = ["equity", "index", "fx", "fund", "commodity", "bond"] as const;

const GROUP_LABELS: Record<string, string> = {
  equity: "Equities",
  index: "Indices",
  fx: "FX",
  fund: "Funds",
  commodity: "Commodities",
  bond: "Bonds and notes",
};

export const groupLabel = (kind: string): string => GROUP_LABELS[kind] ?? kind;

/** Find RICs processes at most this many rows of a selection (ruling 10). */
export const MAX_ROWS = 2000;

const fmt = (n: number): string => n.toLocaleString("en-US");
const str = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);

export type PaneRow =
  | { kind: "heading"; group: string; label: string; total: number | null }
  | {
      kind: "item";
      group: string;
      /** Position among the pickable rows (0-based), for the arrow keys. */
      index: number;
      ric: string;
      ticker: string | null;
      name: string | null;
      exchange: string | null;
      currency: string | null;
      isin: string | null;
      inactive: boolean;
      item: SuggestItem;
    }
  | { kind: "note"; isin: string; title: string; tag: string | null; detail: string }
  /** `retry`: a failure the person can ask again (Enter, or the line's Retry button). */
  | { kind: "status"; text: string; tone: "info" | "warn" | "error"; retry?: true }
  | { kind: "more"; query: string };

/**
 * The ticker a row is shown with: only when its code does not already say it
 * (`MC` beside LVMH.PA; nothing beside SAN.MC or VOLVb.ST, whose ticker is
 * `VOLV B`). Case, blanks and dots apart. The terminal's `shownTicker`.
 */
export function shownTicker(ric: string, ticker: string | null | undefined): string | null {
  if (!ticker) return null;
  const plainly = (text: string) => text.replace(/[\s.]/g, "").toUpperCase();
  const root = ric.replace(/^[./]/, "").split(/[.=]/)[0] ?? "";
  return plainly(root) === plainly(ticker) ? null : ticker;
}

/** The English tag of a status ("called", "matured", "inactive"); none for a live one. */
function statusTag(status: string | null | undefined): string | null {
  return status === "called" ? "Called" : status === "matured" ? "Matured" : status === "inactive" ? "Inactive" : null;
}

/** `2033-02-03` as `03/02/2033`; anything else as it is. */
function day(d: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : d;
}

function noteRow(note: SuggestNote | null | undefined): PaneRow | null {
  if (!note || typeof note !== "object") return null;
  const isin = str(note.isin);
  if (!isin) return null;
  const maturity = str(note.maturity);
  const detail = [str(note.type), maturity ? `matures ${day(maturity)}` : null].filter((p): p is string => !!p).join(" · ");
  return { kind: "note", isin, title: str(note.name) ?? isin, tag: statusTag(note.status), detail };
}

const reasonOf = (e: unknown): string => (e instanceof Error ? e.message : String(e ?? "")) || "unknown error";

/** The state failed because the add-in is signed out: the pane shows its signed-out state instead. */
export function isSignedOutState(state: { status: string; error: unknown }): boolean {
  return state.status === "error" && isSignedOut(state.error);
}

/** The pickable rows of an answer, with their group, in the hub's order. */
function answerItems(a: SuggestAnswer | null): { group: string; item: SuggestItem }[] {
  const out: { group: string; item: SuggestItem }[] = [];
  for (const g of a?.groups ?? []) {
    for (const item of g?.items ?? []) if (item && str(item.ric)) out.push({ group: g.kind, item });
  }
  return out;
}

/**
 * What the list shows for a state of the asker: the note line, the groups
 * (heading, then rows) in the hub's order, the status line, and More results.
 */
export function paneRows(state: AskState<SuggestAnswer>): PaneRow[] {
  if (state.query === null || isSignedOutState(state)) return [];
  const rows: PaneRow[] = [];
  const a = state.status === "done" ? state.answer : null;
  let found = false;
  if (a) {
    const note = noteRow(a.note);
    if (note) {
      rows.push(note);
      found = true;
    }
    let index = 0;
    for (const g of a.groups ?? []) {
      const its = (g?.items ?? []).filter((it) => it && str(it.ric));
      if (!its.length) continue;
      rows.push({ kind: "heading", group: g.kind, label: groupLabel(g.kind), total: typeof g.total === "number" ? g.total : null });
      for (const it of its) {
        rows.push({
          kind: "item",
          group: g.kind,
          index: index++,
          ric: it.ric,
          ticker: shownTicker(it.ric, it.ticker),
          name: str(it.name),
          exchange: str(it.exchange),
          currency: str(it.currency),
          isin: str(it.isin),
          inactive: it.active === false,
          item: it,
        });
      }
    }
    if (index > 0) found = true;
  }
  if (state.status === "waiting" || state.status === "loading") rows.push({ kind: "status", text: "Searching…", tone: "info" });
  else if (state.status === "busy") rows.push({ kind: "status", text: "Busy, retrying…", tone: "warn" });
  else if (state.status === "error") rows.push({ kind: "status", text: `Search failed: ${reasonOf(state.error)}`, tone: "error", retry: true });
  else if (a?.partial) rows.push({ kind: "status", text: "Incomplete results", tone: "warn" });
  else if (a && !found) rows.push({ kind: "status", text: "No results", tone: "info" });
  rows.push({ kind: "more", query: state.query });
  return rows;
}

export type EnterOutcome = { kind: "pick"; item: SuggestItem; group: string } | { kind: "wait" } | { kind: "none" };

/**
 * Enter with no row chosen (spec 4.3): a text shaped like a code is written as
 * typed only when the answer lists it; a code the answer lists in another case
 * (`lvmh.pa` typed, `LVMH.PA` listed) is written as the hub spells it;
 * otherwise Enter takes the first suggestion, and waits for it while the
 * answer for the text on screen is on its way.
 */
export function enterTarget(state: AskState<SuggestAnswer>, typed: string): EnterOutcome {
  const t = typed.trim();
  if (t.length < MIN_CHARS) return { kind: "none" };
  if (state.query !== t) return { kind: "wait" };
  if (state.status === "waiting" || state.status === "loading" || state.status === "busy") return { kind: "wait" };
  if (state.status !== "done") return { kind: "none" };
  const all = answerItems(state.answer);
  const code = /^\S+$/.test(t);
  const exact = code ? all.find((x) => x.item.ric === t) : undefined;
  const cased = code && !exact ? all.find((x) => x.item.ric.toUpperCase() === t.toUpperCase()) : undefined;
  const chosen = exact ?? cased ?? all[0];
  return chosen ? { kind: "pick", item: chosen.item, group: chosen.group } : { kind: "none" };
}

/** The box's new text from the active cell: only while the box is empty, and only a text of a searchable length. */
export function prefill(box: string, cell: { text: string; isText: boolean }): string | null {
  if (box.trim() !== "" || !cell.isText) return null;
  const t = cell.text.trim();
  return t.length >= MIN_CHARS && t.length <= MAX_LEN ? t : null;
}

/** A cell of a sheet, 0-based. */
export interface CellRef {
  sheet: string;
  row: number;
  col: number;
}

/** What a pick writes and tells the hub. */
export interface PickRequest {
  ric: string;
  name?: string | null;
  isin?: string | null;
  /** The group it came from ("all" or none for a More results row of every group). */
  group?: string;
  query?: string;
}

/**
 * What becomes of an Enter that waits for the answer (`query` is the text it
 * was pressed on), now that the asker is in `state`: keep waiting, drop it (a
 * new text: the asker's latest-wins; a failure or nothing found), or pick.
 */
export function pendingDecision(
  query: string,
  state: AskState<SuggestAnswer>
): { kind: "keep" } | { kind: "drop" } | { kind: "pick"; item: SuggestItem; group: string } {
  if (state.query !== query || isSignedOutState(state)) return { kind: "drop" };
  const out = enterTarget(state, query);
  if (out.kind === "wait") return { kind: "keep" };
  if (out.kind === "none") return { kind: "drop" };
  return out;
}

export interface PickFlowDeps {
  /** The active cell, read when the click or the Enter happens. */
  activeCell(): Promise<CellRef>;
  /** Write the RIC into `target` (a value), whatever cell is active by then. */
  write(target: CellRef, pick: PickRequest): Promise<void>;
  picked(target: CellRef, pick: PickRequest): void;
  failed(pick: PickRequest, e: unknown): void;
}

export interface PickFlow {
  /** A row was clicked or chosen. False when ignored: a pick is already in flight (a double-click writes once). */
  pick(p: PickRequest): boolean;
  /**
   * Enter with no row chosen. "retry": the text on screen had failed and is to
   * be asked again (the caller calls the asker's retry), the Enter waiting for
   * it; "pending": the Enter waits for the answer.
   */
  enter(state: AskState<SuggestAnswer>, typed: string): "picked" | "pending" | "retry" | "none" | "ignored";
  /** The asker's state changed: decide a pending Enter. */
  onState(state: AskState<SuggestAnswer>): void;
  /** Drop a pending Enter (new text, sign-out). */
  cancel(): void;
  busy(): boolean;
  pending(): boolean;
}

/**
 * Picking, Office-free: the target cell is the one active when the click or
 * the Enter happened, captured then, and written even if the person has moved
 * since (moving is not cancelling; new text is). One pick at a time.
 */
export function createPickFlow(deps: PickFlowDeps): PickFlow {
  let inFlight = false;
  let waiting: { query: string; target: Promise<CellRef> } | null = null;

  const capture = (): Promise<CellRef> => {
    const t = deps.activeCell();
    t.catch(() => {}); // decided (and reported) when it is used
    return t;
  };
  const run = (p: PickRequest, target: Promise<CellRef>): void => {
    inFlight = true;
    void (async () => {
      let cell: CellRef;
      try {
        cell = await target;
        await deps.write(cell, p);
      } catch (e) {
        inFlight = false;
        deps.failed(p, e);
        return;
      }
      inFlight = false;
      deps.picked(cell, p);
    })();
  };
  const fromItem = (item: SuggestItem, group: string, query: string): PickRequest => ({
    ric: item.ric,
    name: item.name,
    isin: item.isin,
    group,
    query,
  });

  return {
    pick(p) {
      if (inFlight) return false;
      waiting = null;
      run(p, capture());
      return true;
    },
    enter(state, typed) {
      if (inFlight) return "ignored";
      const t = typed.trim();
      if (state.status === "error" && state.query === t && t.length >= MIN_CHARS && !isSignedOutState(state)) {
        waiting = { query: t, target: capture() };
        return "retry";
      }
      const out = enterTarget(state, typed);
      if (out.kind === "pick") {
        waiting = null;
        run(fromItem(out.item, out.group, t), capture());
        return "picked";
      }
      if (out.kind === "wait") {
        waiting = { query: t, target: capture() };
        return "pending";
      }
      waiting = null;
      return "none";
    },
    onState(state) {
      if (!waiting) return;
      const d = pendingDecision(waiting.query, state);
      if (d.kind === "keep") return;
      const w = waiting;
      waiting = null;
      if (d.kind === "pick" && !inFlight) run(fromItem(d.item, d.group, w.query), w.target);
    },
    cancel() {
      waiting = null;
    },
    busy: () => inFlight,
    pending: () => waiting !== null,
  };
}

/** The pager of a More results page that failed: paging goes on from it (the known page count, when there is one). */
export function pagerOnFailure(page: number, knownPages?: number): { canPrev: boolean; canNext: boolean } {
  return { canPrev: page > 0, canNext: knownPages === undefined || page + 1 < knownPages };
}

export interface SelectionRect {
  rowIndex: number;
  columnIndex: number;
  rowCount: number;
  columnCount: number;
}

/**
 * The rows Find RICs reads: the selection's first column, cut to the sheet's
 * used range (a whole-column selection never builds a million-row read) and to
 * MAX_ROWS rows. `target` is null when nothing is left.
 */
export function clampTarget(
  sel: SelectionRect,
  used: { rowIndex: number; rowCount: number } | null,
  max = MAX_ROWS
): { target: { firstRow: number; col: number; count: number } | null; notes: string[] } {
  const notes: string[] = [];
  if (sel.columnCount > 1) notes.push("Only the first column is used.");
  if (!used || used.rowCount <= 0) return { target: null, notes };
  const first = Math.max(sel.rowIndex, used.rowIndex);
  const end = Math.min(sel.rowIndex + sel.rowCount, used.rowIndex + used.rowCount);
  if (end <= first) return { target: null, notes };
  let count = end - first;
  if (count > max) {
    count = max;
    notes.push(`Only the first ${fmt(max)} rows are processed.`);
  }
  return { target: { firstRow: first, col: sel.columnIndex, count }, notes };
}

/** The chosen row after an arrow key (-1: none chosen). */
export function moveChoice(index: number, delta: number, count: number): number {
  if (count <= 0) return -1;
  if (index < 0) return delta > 0 ? 0 : count - 1;
  return Math.min(count - 1, Math.max(0, index + delta));
}

export function confirmText(n: number): string {
  return n === 1
    ? "1 cell in the two columns to the right is not empty and will be overwritten."
    : `${fmt(n)} cells in the two columns to the right are not empty and will be overwritten.`;
}

export const progressText = (done: number, total: number): string => `${fmt(done)} / ${fmt(total)}`;

/** The line a finished run leaves. `total` is the number of rows the run planned to search. */
export function summaryText(r: RunResult, total: number): string {
  if (r.stopped === "declined") return "Nothing written: the overwrite was declined.";
  const counts = `${fmt(r.written)} written, ${fmt(r.none)} not found, ${fmt(r.unavailable)} unavailable, ${fmt(r.skippedEdited)} skipped as edited.`;
  if (r.stopped === "signed-out") return `Stopped, signed out: ${counts}`;
  if (r.cancelled) return `Cancelled: ${counts}`;
  if (total === 0) return "Nothing to search: rows that have a RIC are kept (tick Redo all to search them again).";
  return `Done: ${counts}`;
}

/** Whether the active cell can be switched (spec 4.5): its right neighbour holds a « k sur N » marker and its left one a text. */
export function switchOffer(cText: string, leftText: string): boolean {
  return parseMarker(cText) !== null && leftText.trim() !== "";
}

export interface SwitchItem {
  ric: string;
  name: string;
  type: string;
  itemStatus?: string;
  current: boolean;
}

/** The candidates offered for switching, the current RIC marked; or why there are none. */
export function switchChoices(res: Resolution, currentRic: string): { items: SwitchItem[]; message: string | null } {
  if (res.status === "unavailable") return { items: [], message: `Candidates unavailable: ${res.reason || "unknown error"}` };
  if (res.status !== "ok" || !res.candidates.length) return { items: [], message: "No candidates found." };
  return {
    items: res.candidates.map((c) => ({ ...c, current: c.ric === currentRic })),
    message: null,
  };
}

/**
 * B and C after switching to candidate `index`: the same rules as Find RICs
 * (formatC), the marker kept when the resolution carries one, with k the
 * chosen candidate's 1-based position.
 */
export function switchWrite(res: Resolution, index: number): { b: string; c: string } {
  const c = res.candidates[index];
  const r: Resolution = { status: "ok", ric: c.ric, name: c.name, type: c.type, candidates: res.candidates };
  if (c.itemStatus) r.itemStatus = c.itemStatus;
  if (res.n !== undefined) {
    r.k = index + 1;
    r.n = res.candidates.length;
  }
  if (res.partial) r.partial = true;
  return { b: c.ric, c: formatC(r) };
}

const DROPPED_LABELS: Record<string, string> = {
  chain: "chain",
  expired: "expired",
  isin_page: "contributor page",
  fund_id: "fund identifier",
  not_pair: "not a currency pair",
  no_match: "does not match the name",
};

export const droppedLabel = (reason: string): string => DROPPED_LABELS[reason] ?? reason;

export interface MoreRow {
  index: number;
  ric: string;
  ticker: string | null;
  name: string | null;
  exchange: string | null;
  currency: string | null;
  isin: string | null;
  inactive: boolean;
  exact: boolean;
  dropped: string | null;
  row: SearchAllRow;
}

/** One page of More results, as shown. Pages are 0-based. */
export function moreView(a: SearchAllAnswer): {
  rows: MoreRow[];
  pageLabel: string;
  totalLabel: string | null;
  canPrev: boolean;
  canNext: boolean;
  empty: boolean;
} {
  const rows = (a.rows ?? [])
    .filter((r) => r && str(r.ric))
    .map((r, index) => ({
      index,
      ric: r.ric,
      ticker: shownTicker(r.ric, r.ticker),
      name: str(r.name),
      exchange: str(r.exchange),
      currency: str(r.currency),
      isin: str(r.isin),
      inactive: r.active === false,
      exact: r.exact === true,
      dropped: str(r.dropped),
      row: r,
    }));
  const pages = Math.max(1, a.pages ?? 1);
  const page = a.page ?? 0;
  return {
    rows,
    pageLabel: `Page ${page + 1} of ${pages}`,
    totalLabel: typeof a.total === "number" ? `${fmt(a.total)} result${a.total === 1 ? "" : "s"}` : null,
    canPrev: page > 0,
    canNext: page + 1 < pages,
    empty: rows.length === 0,
  };
}

/** The groups More results can be asked for: All, then each group, with page 0's counts when known. */
export function kindOptions(kinds: Partial<Record<string, number>> | undefined): { kind: string; label: string }[] {
  return [
    { kind: "all", label: "All" },
    ...KIND_ORDER.map((k) => {
      const n = kinds?.[k];
      return { kind: k, label: typeof n === "number" ? `${groupLabel(k)} · ${fmt(n)}` : groupLabel(k) };
    }),
  ];
}

// --- Glue (Office.js and DOM; not unit-tested) -------------------------------------

export interface SearchPaneHooks {
  /** The add-in is signed out (a search or a run met the add-in's own 401). */
  onSignedOut(e: unknown): void;
}

/** A person is waiting: the switch candidates go through the interactive lane. */
const interactiveDeps: ResolveDeps = {
  suggest: (q) => suggest(q, false),
  lookupIsin: (isin) => lookupIsin(isin, false),
  sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
};

const SELECTION_DEBOUNCE_MS = 150;

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

function el(tag: string, cls: string, text?: string | null): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
}

function line(id: string, text: string, tone: "" | "error" | "ok" | "warn" = ""): void {
  const p = $(id);
  p.textContent = text;
  p.className = "msg" + (tone ? ` msg-${tone}` : "");
  p.hidden = !text;
}

let mounted = false;
let hooks: SearchPaneHooks = { onSignedOut: () => {} };
let asker: Asker<SuggestAnswer> | null = null;
let chosen = -1;
let flow: PickFlow | null = null;

interface MoreState {
  query: string;
  kind: string;
  page: number;
  ask: AskState<SearchAllAnswer>;
}
let more: MoreState | null = null;
let moreAsker: Asker<SearchAllAnswer> | null = null;
let moreKinds: Partial<Record<string, number>> | undefined;
/** The page count of the last page that came, for paging on from a failed one. */
let morePages: number | undefined;
/** The options the group selector holds (rebuilt only when they change). */
let kindsShown = "";

interface SwitchState {
  sheet: string;
  row: number;
  col: number;
  left: string;
  currentRic: string;
  currentC: string;
  res: Resolution | null;
  error: string | null;
}
let switching: SwitchState | null = null;
let switchTurn = 0;
let switchBusy = false;

let running: AbortController | null = null;
/** Bumped by a reset (sign-out): a run started before it reports nothing. */
let runGeneration = 0;
let confirmAnswer: ((yes: boolean) => void) | null = null;
let selectionTimer: ReturnType<typeof setTimeout> | null = null;

const searchVisible = (): boolean => !$("panel-search").hidden;

/** Mount the Search tab once (after sign-in). */
export function mountSearchPane(h: SearchPaneHooks): void {
  hooks = h;
  if (mounted) return;
  mounted = true;

  asker = createAsker<SuggestAnswer>((q) => suggest(q, false), { onChange: onAsk });
  flow = createPickFlow({ activeCell: readActiveRef, write: writePick, picked: onPicked, failed: onPickFailed });
  moreAsker = createAsker<SearchAllAnswer>(
    (key) => {
      const { q, kind, page } = JSON.parse(key) as { q: string; kind: string; page: number };
      return searchAll(q, kind, page);
    },
    { delayMs: 0, onChange: onMore }
  );

  $("tab-functions").addEventListener("click", () => showTab("functions"));
  $("tab-search").addEventListener("click", () => showTab("search"));

  const box = $("search-box") as HTMLInputElement;
  box.addEventListener("input", onInput);
  box.addEventListener("keydown", onKey);

  $("more-prev").addEventListener("click", () => more && askMore(more.query, more.kind, more.page - 1));
  $("more-next").addEventListener("click", () => more && askMore(more.query, more.kind, more.page + 1));
  $("more-retry").addEventListener("click", () => moreAsker?.retry());
  $("more-kind").addEventListener("change", () => more && askMore(more.query, ($("more-kind") as HTMLSelectElement).value, 0));
  $("more-close").addEventListener("click", closeMore);

  $("find-run").addEventListener("click", () => void onFindRics());
  $("find-cancel").addEventListener("click", () => {
    running?.abort();
    answerConfirm(false);
  });
  $("find-confirm-yes").addEventListener("click", () => answerConfirm(true));
  $("find-confirm-no").addEventListener("click", () => answerConfirm(false));

  try {
    Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, () => {
      if (selectionTimer) clearTimeout(selectionTimer);
      selectionTimer = setTimeout(() => void onSelection(), SELECTION_DEBOUNCE_MS);
    });
  } catch {
    /* no selection events on this host: the box is filled when the tab opens */
  }
}

/**
 * Forget the search (sign-out). A Find RICs run in progress is aborted, a
 * pending confirmation is declined, and the run reports nothing afterwards.
 */
export function resetSearchPane(): void {
  if (!mounted) return;
  ($("search-box") as HTMLInputElement).value = "";
  asker?.query(null);
  flow?.cancel();
  chosen = -1;
  closeMore();
  runGeneration++;
  running?.abort();
  answerConfirm(false);
  endRun();
  line("find-notes", "");
  line("find-progress", "");
  line("find-summary", "");
  switching = null;
  switchTurn++;
  renderSwitch();
  renderList();
}

/** Show the Functions or the Search tab (also the EAV ribbon's Search button, via taskpane.ts). */
export function showTab(tab: "functions" | "search"): void {
  const search = tab === "search";
  $("panel-functions").hidden = search;
  $("panel-search").hidden = !search;
  $("tab-functions").classList.toggle("tab-on", !search);
  $("tab-search").classList.toggle("tab-on", search);
  $("tab-functions").setAttribute("aria-selected", String(!search));
  $("tab-search").setAttribute("aria-selected", String(search));
  if (search) {
    ($("search-box") as HTMLInputElement).focus();
    void onSelection();
  }
}

// --- Suggestions ---

function onInput(): void {
  chosen = -1;
  flow?.cancel(); // new text drops a pending Enter
  closeMore();
  asker?.query(($("search-box") as HTMLInputElement).value);
}

function pickableCount(): number {
  return asker ? paneRows(asker.state()).filter((r) => r.kind === "item").length : 0;
}

function onKey(ev: KeyboardEvent): void {
  if (!asker || more) return;
  if (ev.key === "ArrowDown" || ev.key === "ArrowUp") {
    ev.preventDefault();
    chosen = moveChoice(chosen, ev.key === "ArrowDown" ? 1 : -1, pickableCount());
    renderList();
  } else if (ev.key === "Enter") {
    ev.preventDefault();
    const rows = paneRows(asker.state());
    const row = rows.find((r) => r.kind === "item" && r.index === chosen);
    if (row && row.kind === "item") pickItem(row.item, row.group);
    else onEnter();
  } else if (ev.key === "Escape") {
    ($("search-box") as HTMLInputElement).value = "";
    onInput();
  }
}

function onEnter(): void {
  if (!asker || !flow) return;
  if (flow.enter(asker.state(), ($("search-box") as HTMLInputElement).value) === "retry") asker.retry();
}

function onAsk(state: AskState<SuggestAnswer>): void {
  if (isSignedOutState(state)) {
    flow?.cancel();
    hooks.onSignedOut(state.error);
    return;
  }
  if (chosen >= 0 && state.status !== "done") chosen = -1;
  renderList();
  flow?.onState(state);
}

function renderList(): void {
  const list = $("search-list");
  list.replaceChildren();
  list.hidden = more !== null;
  if (!asker) return;
  for (const r of paneRows(asker.state())) {
    if (r.kind === "heading") {
      const h = el("div", "res-head", r.label);
      if (r.total !== null) h.appendChild(el("span", "res-count", r.total.toLocaleString("en-US")));
      list.appendChild(h);
    } else if (r.kind === "item") {
      const row = resultRow(r.ric, r.ticker, r.name, [r.exchange, r.currency, r.isin], r.inactive ? "Inactive" : null);
      row.setAttribute("role", "option");
      if (r.index === chosen) row.classList.add("res-chosen");
      if (r.inactive) row.classList.add("res-inactive");
      row.addEventListener("click", () => pickItem(r.item, r.group));
      list.appendChild(row);
    } else if (r.kind === "note") {
      const n = el("div", "res-note");
      const top = el("div", "res-line");
      top.appendChild(el("span", "res-code", r.isin));
      if (r.tag) top.appendChild(el("span", "res-tag", r.tag));
      n.appendChild(top);
      n.appendChild(el("div", "res-name", r.title));
      if (r.detail) n.appendChild(el("div", "res-meta", r.detail));
      list.appendChild(n);
    } else if (r.kind === "status") {
      const p = el("p", `res-status res-${r.tone}`, r.text);
      if (r.retry) {
        const b = el("button", "btn btn-ghost btn-small", "Retry");
        b.addEventListener("click", () => asker?.retry());
        p.appendChild(b);
      }
      list.appendChild(p);
    } else {
      const m = el("button", "res-more btn btn-ghost", `More results for "${r.query}"`);
      m.addEventListener("click", () => openMore(r.query));
      list.appendChild(m);
    }
  }
}

function resultRow(ric: string, ticker: string | null, name: string | null, meta: (string | null)[], tag: string | null): HTMLElement {
  const row = el("div", "res-item");
  const top = el("div", "res-line");
  top.appendChild(el("span", "res-code", ric));
  if (ticker) top.appendChild(el("span", "res-ticker", ticker));
  if (tag) top.appendChild(el("span", "res-tag", tag));
  row.appendChild(top);
  if (name) row.appendChild(el("div", "res-name", name));
  const m = meta.filter((p): p is string => !!p).join(" · ");
  if (m) row.appendChild(el("div", "res-meta", m));
  return row;
}

function pickItem(item: SuggestItem, group: string): void {
  flow?.pick({ ric: item.ric, name: item.name, isin: item.isin, group, query: asker?.state().query ?? undefined });
}

async function readActiveRef(): Promise<CellRef> {
  return Excel.run(async (context) => {
    const cell = context.workbook.getActiveCell();
    cell.load("rowIndex,columnIndex");
    cell.worksheet.load("name");
    await context.sync();
    return { sheet: cell.worksheet.name, row: cell.rowIndex, col: cell.columnIndex };
  });
}

/**
 * Write the RIC into the captured cell (a value), then select the cell below
 * it, but only when that cell is still the active one: a person who moved
 * while an Enter waited keeps their place.
 */
async function writePick(target: CellRef, p: PickRequest): Promise<void> {
  await Excel.run(async (context) => {
    const cell = context.workbook.worksheets.getItem(target.sheet).getRangeByIndexes(target.row, target.col, 1, 1);
    markOwn({ sheet: target.sheet, row: target.row, col: target.col, rows: 1, cols: 1 });
    cell.values = [[literalOf({ v: p.ric })]];
    const active = context.workbook.getActiveCell();
    active.load("rowIndex,columnIndex");
    active.worksheet.load("name");
    await context.sync();
    if (active.worksheet.name === target.sheet && active.rowIndex === target.row && active.columnIndex === target.col) {
      try {
        cell.getOffsetRange(1, 0).select();
        await context.sync();
      } catch {
        /* the last row of the sheet: the selection stays */
      }
    }
  });
}

function onPicked(_target: CellRef, p: PickRequest): void {
  line("search-msg", "");
  tellPick({
    ric: p.ric,
    ...(p.name ? { name: p.name } : {}),
    ...(p.group && p.group !== "all" ? { kind: p.group } : {}),
    ...(p.isin ? { isin: p.isin } : {}),
    ...(p.query ? { query: p.query } : {}),
  });
  // An empty box takes the next cell's text (walking down a column).
  const box = $("search-box") as HTMLInputElement;
  box.value = "";
  onInput();
  box.focus();
}

function onPickFailed(p: PickRequest, e: unknown): void {
  line("search-msg", `Could not write ${p.ric}: ${reasonOf(e)}`, "error");
}

// --- More results ---

function openMore(query: string): void {
  more = { query, kind: "all", page: 0, ask: { query: null, status: "idle", answer: null, error: null } };
  moreKinds = undefined;
  morePages = undefined;
  $("search-more").hidden = false;
  askMore(query, "all", 0);
}

function closeMore(): void {
  if (!more) return;
  more = null;
  moreAsker?.query(null);
  $("search-more").hidden = true;
  renderList();
}

function askMore(query: string, kind: string, page: number): void {
  if (!more || page < 0) return;
  if (kind !== more.kind) morePages = undefined; // another group's page count says nothing of this one
  more.kind = kind;
  more.page = page;
  moreAsker?.query(JSON.stringify({ q: query, kind, page }));
}

function onMore(state: AskState<SearchAllAnswer>): void {
  if (!more) return;
  if (isSignedOutState(state)) {
    hooks.onSignedOut(state.error);
    return;
  }
  more.ask = state;
  const a = state.answer;
  if (a && a.page === 0 && a.kind === "all" && a.kinds) moreKinds = a.kinds;
  if (a) morePages = a.pages;
  renderMore();
}

function renderMore(): void {
  if (!more) return;
  renderList();
  const select = $("more-kind") as HTMLSelectElement;
  // Rebuilt only when the options change: an answer that lands while the list
  // is open must not close it.
  const options = kindOptions(moreKinds);
  const key = JSON.stringify(options);
  if (key !== kindsShown) {
    kindsShown = key;
    select.replaceChildren();
    for (const o of options) {
      const opt = document.createElement("option");
      opt.value = o.kind;
      opt.textContent = o.label;
      select.appendChild(opt);
    }
  }
  if (select.value !== more.kind) select.value = more.kind;
  $("more-title").textContent = `More results for "${more.query}"`;

  const list = $("more-list");
  list.replaceChildren();
  const s = more.ask;
  const a = s.status === "done" ? s.answer : null;
  const prev = $("more-prev") as HTMLButtonElement;
  const next = $("more-next") as HTMLButtonElement;
  if (a) {
    const v = moreView(a);
    for (const r of v.rows) {
      const tag = r.exact ? "ISIN" : r.inactive ? "Inactive" : null;
      const row = resultRow(r.ric, r.ticker, r.name, [r.exchange, r.currency, r.isin, r.dropped ? droppedLabel(r.dropped) : null], tag);
      if (r.inactive || r.dropped) row.classList.add("res-inactive");
      row.addEventListener("click", () =>
        flow?.pick({ ric: r.ric, name: r.name, isin: r.isin, group: more?.kind, query: more?.query })
      );
      list.appendChild(row);
    }
    $("more-page").textContent = [v.pageLabel, v.totalLabel].filter(Boolean).join(" · ");
    prev.disabled = !v.canPrev;
    next.disabled = !v.canNext;
    line("more-status", v.empty ? "No results" : "");
    $("more-retry").hidden = true;
  } else {
    // A failed page: paging goes on from it, and Retry asks it again.
    const failed = s.status === "error";
    const pager = pagerOnFailure(more.page, morePages);
    prev.disabled = !failed || !pager.canPrev;
    next.disabled = !failed || !pager.canNext;
    $("more-page").textContent = `Page ${more.page + 1}`;
    $("more-retry").hidden = !failed;
    if (s.status === "busy") line("more-status", "Busy, retrying…", "warn");
    else if (failed) line("more-status", `Search failed: ${reasonOf(s.error)}`, "error");
    else line("more-status", "Searching…");
  }
}

// --- The active cell: pre-fill and switching ---

interface ActiveCell {
  sheet: string;
  row: number;
  col: number;
  text: string;
  isText: boolean;
  left: string;
  right: string;
}

async function readActive(): Promise<ActiveCell> {
  return Excel.run(async (context) => {
    const cell = context.workbook.getActiveCell();
    cell.load("text,valueTypes,rowIndex,columnIndex");
    cell.worksheet.load("name");
    const right = cell.getOffsetRange(0, 1);
    right.load("text");
    await context.sync();
    let left = "";
    if (cell.columnIndex > 0) {
      const l = cell.getOffsetRange(0, -1);
      l.load("text");
      await context.sync();
      left = String(l.text?.[0]?.[0] ?? "");
    }
    return {
      sheet: cell.worksheet.name,
      row: cell.rowIndex,
      col: cell.columnIndex,
      text: String(cell.text?.[0]?.[0] ?? ""),
      isText: cell.valueTypes?.[0]?.[0] === Excel.RangeValueType.string,
      left,
      right: String(right.text?.[0]?.[0] ?? ""),
    };
  });
}

async function onSelection(): Promise<void> {
  if (!mounted || !searchVisible()) return;
  let cell: ActiveCell;
  try {
    cell = await readActive();
  } catch {
    return; // e.g. a chart is selected, or the cell is being edited
  }
  const box = $("search-box") as HTMLInputElement;
  const text = prefill(box.value, cell);
  if (text !== null) {
    box.value = text;
    onInput();
  }
  void offerSwitch(cell);
}

async function offerSwitch(cell: ActiveCell): Promise<void> {
  const turn = ++switchTurn;
  if (!switchOffer(cell.right, cell.left) || cell.text.trim() === "") {
    switching = null;
    renderSwitch();
    return;
  }
  const same = switching && switching.sheet === cell.sheet && switching.row === cell.row && switching.col === cell.col && switching.left === cell.left;
  switching = { sheet: cell.sheet, row: cell.row, col: cell.col, left: cell.left, currentRic: cell.text, currentC: cell.right, res: same ? switching!.res : null, error: null };
  renderSwitch();
  if (same && switching.res) return;
  try {
    const res = await resolveRow(cell.left.trim(), interactiveDeps);
    if (turn !== switchTurn || !switching) return;
    switching.res = res;
  } catch (e) {
    if (turn !== switchTurn || !switching) return;
    if (isSignedOut(e)) {
      switching = null;
      renderSwitch();
      hooks.onSignedOut(e);
      return;
    }
    switching.error = reasonOf(e);
  }
  renderSwitch();
}

function renderSwitch(): void {
  const box = $("search-switch");
  box.replaceChildren();
  box.hidden = switching === null;
  if (!switching) return;
  box.appendChild(el("h2", "label", "Choose another"));
  box.appendChild(el("p", "hint", `Candidates for "${switching.left.trim()}"`));
  if (switching.error) {
    box.appendChild(el("p", "msg msg-error", `Candidates unavailable: ${switching.error}`));
    return;
  }
  if (!switching.res) {
    box.appendChild(el("p", "msg", "Searching…"));
    return;
  }
  const v = switchChoices(switching.res, switching.currentRic);
  if (v.message) {
    box.appendChild(el("p", "msg", v.message));
    return;
  }
  v.items.forEach((c, i) => {
    const row = resultRow(c.ric, null, c.name, [c.type], c.current ? "Current" : c.itemStatus ?? null);
    if (c.current) row.classList.add("res-current");
    else row.addEventListener("click", () => void doSwitch(i));
    box.appendChild(row);
  });
}

/** Rewrite B (the active cell) and C with the chosen candidate, unless the user changed them meanwhile. */
async function doSwitch(index: number): Promise<void> {
  const s = switching;
  if (!s || !s.res || switchBusy) return;
  const w = switchWrite(s.res, index);
  switchBusy = true;
  try {
    const wrote = await Excel.run(async (context) => {
      const ws = context.workbook.worksheets.getItem(s.sheet);
      const bc = ws.getRangeByIndexes(s.row, s.col, 1, 2);
      bc.load("text");
      await context.sync();
      if (String(bc.text[0][0]) !== s.currentRic || String(bc.text[0][1]) !== s.currentC) return false;
      markOwn({ sheet: s.sheet, row: s.row, col: s.col, rows: 1, cols: 2 });
      bc.values = [[literalOf({ v: w.b }), literalOf({ v: w.c })]];
      await context.sync();
      return true;
    });
    if (!wrote) {
      line("search-msg", "The cells changed meanwhile: nothing was written.", "error");
      return;
    }
  } catch (e) {
    line("search-msg", `Could not write ${w.b}: ${reasonOf(e)}`, "error");
    return;
  } finally {
    switchBusy = false;
  }
  line("search-msg", "");
  const c = s.res.candidates[index];
  tellPick({ ric: c.ric, ...(c.name ? { name: c.name } : {}), query: s.left.trim() });
  if (switching === s) {
    s.currentRic = w.b;
    s.currentC = w.c;
    renderSwitch();
  }
}

// --- Find RICs ---

function askConfirm(n: number): Promise<boolean> {
  $("find-confirm-text").textContent = `${confirmText(n)} Continue?`;
  $("find-confirm").hidden = false;
  return new Promise((resolve) => {
    confirmAnswer = resolve;
  });
}

function answerConfirm(yes: boolean): void {
  $("find-confirm").hidden = true;
  const answer = confirmAnswer;
  confirmAnswer = null;
  answer?.(yes);
}

async function readSelection(): Promise<{
  sheet: string;
  sel: SelectionRect;
  used: { rowIndex: number; rowCount: number } | null;
}> {
  return Excel.run(async (context) => {
    const sel = context.workbook.getSelectedRange();
    sel.load("rowIndex,columnIndex,rowCount,columnCount");
    sel.worksheet.load("name");
    const used = sel.worksheet.getUsedRangeOrNullObject(true);
    used.load("rowIndex,rowCount");
    await context.sync();
    return {
      sheet: sel.worksheet.name,
      sel: { rowIndex: sel.rowIndex, columnIndex: sel.columnIndex, rowCount: sel.rowCount, columnCount: sel.columnCount },
      used: used.isNullObject ? null : { rowIndex: used.rowIndex, rowCount: used.rowCount },
    };
  });
}

async function onFindRics(): Promise<void> {
  if (running) return;
  line("find-notes", "");
  line("find-progress", "");
  line("find-summary", "");
  let picked: Awaited<ReturnType<typeof readSelection>>;
  try {
    picked = await readSelection();
  } catch (e) {
    line("find-summary", `Select one column of cells first (${reasonOf(e)}).`, "error");
    return;
  }
  const { target, notes } = clampTarget(picked.sel, picked.used);
  line("find-notes", notes.join(" "), notes.length ? "warn" : "");
  if (!target) {
    line("find-summary", "Nothing to search in the selection.");
    return;
  }
  const ctl = new AbortController();
  const generation = runGeneration;
  running = ctl;
  ($("find-run") as HTMLButtonElement).disabled = true;
  $("find-cancel").hidden = false;
  let total = 0;
  try {
    const result = await runFindRics(
      excelSheetIO(),
      { sheet: picked.sheet, ...target },
      {
        redoAll: ($("find-redo") as HTMLInputElement).checked,
        signal: ctl.signal,
        confirm: askConfirm,
        onProgress: (done, t) => {
          total = t;
          if (generation === runGeneration) line("find-progress", progressText(done, t));
        },
      }
    );
    if (generation !== runGeneration) return; // signed out meanwhile: the run says nothing
    line("find-summary", summaryText(result, total), result.stopped === "signed-out" ? "error" : result.cancelled ? "warn" : "ok");
    if (result.stopped === "signed-out") hooks.onSignedOut(new ApiError(401, "Session expired — sign in again."));
  } catch (e) {
    if (generation !== runGeneration) return;
    if (isSignedOut(e)) hooks.onSignedOut(e);
    else line("find-summary", `Find RICs failed: ${reasonOf(e)}`, "error");
  } finally {
    if (running === ctl) {
      answerConfirm(false);
      endRun();
    }
  }
}

/** No run in progress: the button back, Cancel gone. */
function endRun(): void {
  running = null;
  ($("find-run") as HTMLButtonElement).disabled = false;
  $("find-cancel").hidden = true;
}
